import type { MarlinStoreDb } from "../infra/db";
import { SELECTED_CAMERA_KEY } from "../infra/state";
import { pruneLayoutReferences } from "../logic/layouts/references";
import type {
  CageRecord,
  MachineRecord,
  SelectionRecord,
  SingleSite,
  SiteMap,
} from "../types";
import { assertValidSite } from "./validate";

export type SaveSiteResult = {
  sites: SiteMap;
  previous: SingleSite | undefined;
};

const stripCages = ({ cages: _cages, ...site }: MachineRecord): SingleSite =>
  site;

export const getAllSites = async (db: MarlinStoreDb): Promise<SiteMap> => {
  const sites: SiteMap = {};
  for (const machine of await db.machines.toArray())
    sites[machine._id] = stripCages(machine);
  return sites;
};

export const getMachine = (
  db: MarlinStoreDb,
  id: number,
): Promise<MachineRecord | undefined> => db.machines.get(id);

export const getMachines = (db: MarlinStoreDb): Promise<MachineRecord[]> =>
  db.machines.toArray();

export const saveSite = (
  db: MarlinStoreDb,
  site: SingleSite,
): Promise<SaveSiteResult> => {
  assertValidSite(site);
  return db.transaction("rw", db.machines, async () => {
    const all = await db.machines.toArray();
    let existing = site.machine_id
      ? all.find((m) => m.machine_id === site.machine_id)
      : undefined;
    const cages = existing?.cages;
    const repointed = existing && site._id !== existing._id;
    if (existing && repointed) {
      if (site._id > 0) await db.machines.delete(site._id);
      await db.machines.delete(existing._id);
      existing = undefined;
    }
    const nextId = all.reduce((highest, m) => Math.max(highest, m._id), 0) + 1;
    const _id =
      existing?._id ?? (repointed ? nextId : site._id > 0 ? site._id : nextId);
    const previousRow =
      existing ?? (repointed ? undefined : all.find((m) => m._id === _id));
    await db.machines.put({
      ...site,
      _id,
      cages: cages ?? previousRow?.cages ?? [],
    });
    return {
      sites: await getAllSites(db),
      previous: previousRow ? stripCages(previousRow) : undefined,
    };
  });
};

export const purgeMachineReferences = (
  db: MarlinStoreDb,
  machineId: number,
  siteIds: ReadonlySet<number>,
): Promise<void> =>
  db.transaction("rw", db.tables, async () => {
    for (const screen of await db.screens.toArray())
      await db.screens.put(
        pruneLayoutReferences(screen, {
          keepMachine: (id) => id !== machineId,
          keepSite: (id) => !siteIds.has(id),
        }),
      );
    await db.offsets.where("machineId").equals(machineId).delete();
    const selected = (await db.settings.get(SELECTED_CAMERA_KEY))?.value as
      | SelectionRecord
      | null
      | undefined;
    if (selected?.machineId === machineId)
      await db.settings.delete(SELECTED_CAMERA_KEY);
  });

export const removeMachine = (
  db: MarlinStoreDb,
  machineId: number,
): Promise<void> =>
  db.transaction("rw", db.tables, async () => {
    const rows = await db.machines
      .where("machine_id")
      .equals(machineId)
      .toArray();
    await db.machines.bulkDelete(rows.map((m) => m._id));
    const siteIds = new Set(rows.map((m) => m.site_id));
    for (const remaining of await db.machines.toArray())
      siteIds.delete(remaining.site_id);
    await purgeMachineReferences(db, machineId, siteIds);
  });

export const removeSite = async (
  db: MarlinStoreDb,
  site: SingleSite,
): Promise<SiteMap> => {
  if (site.machine_id) await removeMachine(db, site.machine_id);
  else await db.machines.delete(site._id);
  return getAllSites(db);
};

export const getMachinesLoaded = async (
  db: MarlinStoreDb,
): Promise<Record<number, CageRecord[]>> => {
  const loaded: Record<number, CageRecord[]> = {};
  for (const machine of await db.machines.toArray())
    if (machine.machine_id) loaded[machine.machine_id] = machine.cages;
  return loaded;
};

export const replaceSites = (
  db: MarlinStoreDb,
  sites: SingleSite[],
): Promise<void> =>
  db.transaction("rw", db.machines, async () => {
    const loaded = await getMachinesLoaded(db);
    await db.machines.clear();
    await db.machines.bulkPut(
      sites.map((site) => ({ ...site, cages: loaded[site.machine_id] ?? [] })),
    );
  });
